import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Button, Grid, TextField, IconButton, Tooltip,
  LinearProgress, Autocomplete, Alert, CircularProgress, Chip, Divider,
  Table, TableContainer, TableHead, TableBody, TableRow, TableCell, Switch, FormControlLabel,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SavingsIcon from '@mui/icons-material/Savings';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  addBudget, updateBudget, deleteBudget, fetchBudgets, fetchBudgetSummary,
} from '../slices/budgetSlice';
import { fetchTransactions } from '../slices/transactionSlice';
import categoryService from '../services/categoryService';
import merchantRuleService from '../services/merchantRuleService';
import recurringService from '../services/recurringService';
import budgetService from '../services/budgetService';
import goalService from '../services/goalService';
import { formatAmount } from '../utils/formatHelpers';
import { getCategoryEmoji, buildEmojiOverrides } from '../utils/categoryEmoji';
import { esAdmin } from '../utils/roles';
import { useLanguage } from '../contexts/LanguageContext';

// 29 ago 2026 (pedido de Augusto: "necesito exista una opción IA para
// que construya el presupuesto... esto debe de ser pagado por el
// usuario y tener su precio. Ser parte del paquete premium"): mismo
// criterio que ADMIN_EMAILS/isAdminUser en Reports.js -- solo cosmético,
// el backend vuelve a validar el email antes de saltarse la cuota (ver
// admin_override en ai_suggest_budget).
// 17 sep 2026: la lista de emails vive ahora en utils/roles.js.
// Planes que sí tienen acceso al constructor de IA -- ver
// PLANES_CON_IA_PRESUPUESTO en deps.py (debe mantenerse igual). Esto es
// solo para decidir qué botón mostrar de entrada; el backend es quien
// realmente lo hace cumplir (403 si no calza).
const PLANES_CON_IA_PRESUPUESTO = ['pro', 'negocio_basico', 'negocio_pro'];

// 29 ago 2026 (pedido de Augusto: "vamos a crear el presupuesto -- es
// una manera para que el usuario alcance sus metas de ahorro y luego
// inversión a largo plazo... mucho del tema financiero es reeducación
// de la persona, y si la persona lo ve, más fácil le es cambiar").
//
// Reemplaza por completo la versión anterior de este componente, que
// estaba escrita contra campos que nunca existieron en ningún backend
// (category/amount/spent/description en inglés) -- ver budgetSlice.js
// para el detalle del contrato real. Presupuesto = un monto mensual en
// SOLES por categoría que el usuario no debería superar (ver
// AskUserQuestion: solo soles por ahora, igual que el resto de la app
// nunca mezcla soles y dólares en un mismo total). El backend
// (GET /budgets/summary) ya calcula "gastado este mes" por categoría --
// acá solo se muestra, no se vuelve a sumar nada.
//
// Mismas 9 categorías base + normalización que TransactionsPage.js (cada
// página del proyecto mantiene su propia copia local de este pequeño
// bloque -- convención ya establecida en el código, ver categorias/
// normalizarCategoria/traducirCategoria ahí).
const categoriasBase = [
  'Salario', 'Alquiler', 'Alimentación', 'Transporte', 'Servicios',
  'Entretenimiento', 'Salud', 'Educación', 'Otro',
];
// Escapes \uXXXX (no los caracteres Unicode literales) a propósito -- ver
// la nota del 20 ago 2026 en TransactionsPage.js: los caracteres
// combinantes/invisibles literales corren riesgo de corromperse al pasar
// por un editor web.
const normalizarCategoria = (s) => {
  if (!s) return '';
  const nfc = s.normalize('NFC');
  const sinTildes = nfc.normalize('NFKD').replace(new RegExp('[\\u0300-\\u036f]', 'g'), '');
  const sinInvisibles = sinTildes.replace(new RegExp('[\\u200b\\u200c\\u200d\\ufeff\\u00ad]', 'g'), '');
  return sinInvisibles.trim().toLowerCase().replace(/\s+/g, ' ');
};
const CATEGORIA_BASE_LABELS = {
  food: { es: 'Alimentación', en: 'Food & Dining' },
  transport: { es: 'Transporte', en: 'Transport' },
  bills: { es: 'Facturas y Servicios', en: 'Bills & Utilities' },
  shopping: { es: 'Compras', en: 'Shopping' },
  entertainment: { es: 'Entretenimiento', en: 'Entertainment' },
  salary: { es: 'Salario', en: 'Salary' },
  other: { es: 'Otro', en: 'Other' },
  otro: { es: 'Otro', en: 'Other' },
};
const traducirCategoria = (categoria, language) => {
  if (!categoria) return categoria;
  const entry = CATEGORIA_BASE_LABELS[categoria.trim().toLowerCase()];
  if (!entry) return categoria;
  return language === 'en' ? entry.en : entry.es;
};

// 30 ago 2026 (pedido de Augusto: "se necesita identificar si es por mes
// y de qué mes se está hablando"): tanto la meta mensual como "gastado
// este mes" en la tabla de categorías siempre fueron el mes EN CURSO,
// pero en ningún lado decía cuál -- alguien que vuelve a la página en
// otro momento del mes (o simplemente no se acuerda) no tenía forma de
// confirmarlo. Esto NO agrega un selector de otros meses (ver
// AskUserQuestion: solo la etiqueta por ahora) -- toLocaleDateString ya
// da el nombre del mes traducido, solo se capitaliza la primera letra
// porque en es-PE viene en minúscula ("agosto de 2026").
const formatMesActual = (language) => {
  const texto = new Date().toLocaleDateString(language === 'es' ? 'es-PE' : 'en-US', {
    month: 'long',
    year: 'numeric',
  });
  return texto.charAt(0).toUpperCase() + texto.slice(1);
};

const Budgets = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const budgets = useSelector((state) => state.budgets.list);
  const summary = useSelector((state) => state.budgets.summary);
  const loading = useSelector((state) => state.budgets.loading);
  const error = useSelector((state) => state.budgets.error);
  const transactions = useSelector((state) => state.transactions.list);
  const authUser = useSelector((state) => state.auth.user);
  const isAdminUser = esAdmin(authUser);
  // 30 ago 2026 (Augusto: "solo veo la opción de hacer el presupuesto
  // manual" -- su propia cuenta está en subscription_plan="free" en la
  // base, el admin_override nunca cambia esa columna): mismo bypass que
  // ya tiene require_pro_plan_or_above en el backend -- si no se refleja
  // acá también, un admin vería el botón bloqueado y ni siquiera
  // intentaría hacer clic, aunque el backend ya lo dejaría pasar.
  const tienePlanIA = PLANES_CON_IA_PRESUPUESTO.includes(authUser?.subscription_plan) || isAdminUser;

  const [customCategories, setCustomCategories] = useState([]);
  const [merchantRules, setMerchantRules] = useState([]);
  // 30 ago 2026 (pregunta de Augusto: "con respecto a gastos recurrentes
  // fijos, qué me sugieres con respecto al presupuesto?"): un gasto
  // recurrente fijo (alquiler, Netflix, el seguro) no se convierte solo
  // en una transacción real -- son plantillas que solo alimentan el
  // Calendario, así que "gastado este mes" (ver summary, que viene de
  // gastado_mes_categoria en el backend) no las incluye a menos que se
  // registren a mano cada vez. Se trae la lista de egresos recurrentes
  // acá solo para mostrar, junto al presupuesto de cada categoría, cuánto
  // de ese presupuesto ya está comprometido por gasto fijo -- un piso de
  // referencia, no algo que se sume a "gastado". Ver pisoRecurrentesPorCategoria.
  const [recurringExpenses, setRecurringExpenses] = useState([]);

  useEffect(() => {
    dispatch(fetchBudgets());
    dispatch(fetchBudgetSummary());
    dispatch(fetchTransactions());
    categoryService.getAll().then((res) => setCustomCategories(res.data)).catch(() => {});
    merchantRuleService.getAll().then((res) => setMerchantRules(res.data)).catch(() => {});
    recurringService.getExpenses().then((res) => setRecurringExpenses(res.data)).catch(() => {});
  }, [dispatch]);

  const emojiOverrides = useMemo(() => buildEmojiOverrides(customCategories), [customCategories]);

  // 30 ago 2026 (pedido de Augusto: "lo que se necesita es la meta, que
  // no está definida" -- ver AskUserQuestion: mensual + largo plazo,
  // avance = ingresos reales menos gastos reales): "goal" es la meta tal
  // cual está guardada, "progress" es el avance ya calculado por el
  // backend (ver GET /savings-goal/progress). goalForm es el borrador de
  // edición -- separado de "goal" para poder cancelar sin perder lo
  // guardado si el usuario abre el formulario y se arrepiente.
  const [goal, setGoal] = useState(null);
  const [progress, setProgress] = useState(null);
  const [goalEditing, setGoalEditing] = useState(false);
  const [goalSaving, setGoalSaving] = useState(false);
  const [goalForm, setGoalForm] = useState({
    meta_mensual: '',
    definirLargoPlazo: false,
    meta_largo_plazo_monto: '',
    meta_largo_plazo_fecha: '',
  });

  const cargarMeta = () => {
    goalService.get().then((res) => {
      setGoal(res.data);
      setGoalForm({
        meta_mensual: res.data.meta_mensual ?? '',
        definirLargoPlazo: !!res.data.meta_largo_plazo_monto,
        meta_largo_plazo_monto: res.data.meta_largo_plazo_monto ?? '',
        meta_largo_plazo_fecha: res.data.meta_largo_plazo_fecha ?? '',
      });
    }).catch(() => {});
    goalService.getProgress().then((res) => setProgress(res.data)).catch(() => {});
  };
  useEffect(cargarMeta, []);

  const handleGuardarMeta = async () => {
    setGoalSaving(true);
    try {
      const payload = {
        meta_mensual: goalForm.meta_mensual === '' ? null : parseFloat(goalForm.meta_mensual),
        meta_largo_plazo_monto: goalForm.definirLargoPlazo && goalForm.meta_largo_plazo_monto !== ''
          ? parseFloat(goalForm.meta_largo_plazo_monto)
          : null,
        meta_largo_plazo_fecha: goalForm.definirLargoPlazo && goalForm.meta_largo_plazo_fecha
          ? goalForm.meta_largo_plazo_fecha
          : null,
      };
      const { data } = await goalService.update(payload);
      setGoal(data);
      setGoalEditing(false);
      const { data: prog } = await goalService.getProgress();
      setProgress(prog);
    } catch {
      // silencioso a propósito -- es un formulario chico, sin state de
      // error propio todavía; si falla, el usuario ve que no cambió nada.
    } finally {
      setGoalSaving(false);
    }
  };

  // 29 ago 2026: constructor de presupuesto con IA. Sigue el mismo patrón
  // que "Pregúntale a la IA" en Reports.js -- llamada directa (no un
  // thunk de Redux, esto no es una lista que se cachee) con estado local
  // para loading/error, y distingue 403 (plan sin acceso, no hay bypass)
  // de 402 (sin cuota de páginas, con bypass de admin).
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiPlanBlocked, setAiPlanBlocked] = useState(false);
  const [aiQuotaExceeded, setAiQuotaExceeded] = useState(false);
  const [aiResumen, setAiResumen] = useState(null);
  const [aiSugerencias, setAiSugerencias] = useState([]);
  const [aiAplicando, setAiAplicando] = useState(null); // categoria en proceso de aplicarse individualmente
  const [aiAplicandoTodas, setAiAplicandoTodas] = useState(false);
  // 30 ago 2026 (pedido de Augusto: "no permite ajustar los montos o
  // eliminar las variables solo aprobar"): antes cada tarjeta solo podía
  // aceptar el monto tal cual lo sugirió la IA. Este mapa {categoria:
  // monto} guarda el valor editado a mano para cada tarjeta -- si el
  // usuario no tocó el campo, se sigue usando sug.presupuesto_sugerido
  // (ver montoParaSugerencia más abajo). Se reinicia cada vez que llega
  // una corrida nueva de sugerencias.
  const [montosEditados, setMontosEditados] = useState({});
  const montoParaSugerencia = (sug) => {
    const editado = montosEditados[sug.categoria];
    return editado === undefined || editado === '' ? sug.presupuesto_sugerido : parseFloat(editado) || 0;
  };

  const handleAiSuggest = async (adminOverride = false) => {
    setAiLoading(true);
    setAiError(null);
    setAiPlanBlocked(false);
    setAiQuotaExceeded(false);
    try {
      const { data } = await budgetService.aiSuggest(adminOverride);
      setAiResumen(data.resumen);
      setAiSugerencias(data.sugerencias || []);
      setMontosEditados({});
      // 30 ago 2026 (Augusto: "el Asistente de Presupuesto no hace nada"):
      // antes solo se avisaba si !data.tiene_datos (sin historial
      // suficiente) -- pero si Claude no devolvió el JSON esperado esa
      // corrida, el backend igual responde tiene_datos:true con
      // sugerencias:[] (ver budgets.py::ai_suggest_budget, "No se pudo
      // generar la sugerencia esta vez"), y ese resumen nunca se mostraba:
      // el bloque de sugerencias solo se monta con sugerencias.length > 0,
      // así que el botón terminaba de cargar sin ningún feedback visible.
      // Ahora se avisa siempre que la corrida termine sin sugerencias, sin
      // importar la causa.
      if (!data.sugerencias || data.sugerencias.length === 0) {
        setAiError(
          data.resumen ||
            (language === 'es'
              ? 'No se pudo generar ninguna sugerencia esta vez. Inténtalo de nuevo en unos minutos.'
              : "Couldn't generate any suggestions this time. Try again in a few minutes.")
        );
      }
    } catch (err) {
      const status = err?.response?.status;
      setAiPlanBlocked(status === 403);
      setAiQuotaExceeded(status === 402);
      setAiError(
        err?.response?.data?.detail ||
          (language === 'es'
            ? 'No se pudo construir el presupuesto con IA. Intenta de nuevo en unos segundos.'
            : "Couldn't build the AI budget. Try again in a few seconds.")
      );
    } finally {
      setAiLoading(false);
    }
  };

  // Aplica una sugerencia: crea el presupuesto si la categoría todavía no
  // tiene uno, o actualiza el monto si ya existe (ej. el usuario ya había
  // puesto uno a mano y quiere reemplazarlo por el sugerido). Usa el
  // monto EDITADO si el usuario lo tocó (ver montoParaSugerencia), no
  // necesariamente el que sugirió la IA tal cual.
  const aplicarSugerencia = async (sug) => {
    setAiAplicando(sug.categoria);
    try {
      const monto = montoParaSugerencia(sug);
      const existente = budgets.find((b) => b.categoria === sug.categoria);
      if (existente) {
        await dispatch(updateBudget({ id: existente.id, data: { monto } })).unwrap();
      } else {
        await dispatch(addBudget({ categoria: sug.categoria, monto })).unwrap();
      }
      setAiSugerencias((prev) => prev.filter((s) => s.categoria !== sug.categoria));
    } catch {
      // el error ya queda en state.budgets.error, se muestra arriba.
    } finally {
      setAiAplicando(null);
    }
  };

  const aplicarTodasLasSugerencias = async () => {
    setAiAplicandoTodas(true);
    const pendientes = [...aiSugerencias];
    for (const sug of pendientes) {
      // eslint-disable-next-line no-await-in-loop
      await aplicarSugerencia(sug);
    }
    setAiAplicandoTodas(false);
  };

  // 30 ago 2026 (pedido de Augusto: "eliminar las variables" -- descartar
  // una sugerencia puntual sin aplicarla, ej. una categoría de un solo
  // mes con "baja confianza" que el usuario prefiere decidir a mano
  // desde "Agregar presupuesto"). Solo quita la tarjeta de la vista, no
  // llama al backend -- no hay nada que crear ni borrar todavía.
  const descartarSugerencia = (categoria) => {
    setAiSugerencias((prev) => prev.filter((s) => s.categoria !== categoria));
    setMontosEditados((prev) => {
      const { [categoria]: _omitida, ...resto } = prev;
      return resto;
    });
  };

  // Misma idea que allCategorias en TransactionsPage.js: unión de las
  // categorías base, las personalizadas del usuario y las que ya
  // aparecen en reglas de proveedor, sin duplicados (comparando por la
  // forma traducida y normalizada). Es "la misma lista de categorías que
  // se consumen mes a mes" que pidió Augusto -- exactamente las mismas
  // que el usuario ya ve al categorizar una transacción.
  const todasLasCategorias = useMemo(() => {
    const nombres = [
      ...categoriasBase,
      ...customCategories.map((c) => c.nombre),
      ...merchantRules.map((r) => r.categoria),
    ];
    const vistos = new Set();
    const resultado = [];
    nombres.forEach((n) => {
      const key = normalizarCategoria(traducirCategoria(n, 'es'));
      if (!key || vistos.has(key)) return;
      vistos.add(key);
      resultado.push(n);
    });
    return resultado.sort((a, b) =>
      traducirCategoria(a, language).localeCompare(traducirCategoria(b, language), 'es', { sensitivity: 'base' })
    );
  }, [customCategories, merchantRules, language]);

  const categoriasConPresupuesto = useMemo(
    () => new Set(budgets.map((b) => normalizarCategoria(traducirCategoria(b.categoria, 'es')))),
    [budgets]
  );
  const categoriasDisponibles = useMemo(
    () => todasLasCategorias.filter((c) => !categoriasConPresupuesto.has(normalizarCategoria(traducirCategoria(c, 'es')))),
    [todasLasCategorias, categoriasConPresupuesto]
  );

  // Cuánto gastó el usuario en total este mes (todas las categorías, solo
  // soles) -- para dar contexto de qué tan grande es la categoría dentro
  // de su gasto total, sin inventar una moneda convertida.
  const gastoTotalMesSoles = useMemo(() => {
    const ahora = new Date();
    return transactions
      .filter((t) => t.tipo === 'gasto' && (!t.moneda || t.moneda.toUpperCase() === 'PEN') && t.fecha)
      .filter((t) => {
        const f = new Date(t.fecha);
        return f.getFullYear() === ahora.getFullYear() && f.getMonth() === ahora.getMonth();
      })
      .reduce((sum, t) => sum + (t.monto || 0), 0);
  }, [transactions]);

  // Suma de egresos recurrentes ACTIVOS y en soles por categoría (misma
  // clave normalizada que categoriasConPresupuesto arriba, para que
  // "Servicios" y "servicios" -- o su traducción -- calcen con el
  // presupuesto real). Es solo un piso de referencia: no se resta de
  // nada ni se compara contra "gastado" -- por eso no lleva ningún color
  // de alerta (Augusto: "esas nunca se van a pasar", porque no está
  // comparando gasto real contra un límite, solo mostrando cuánto de tu
  // presupuesto ya está comprometido por un pago fijo conocido).
  const pisoRecurrentesPorCategoria = useMemo(() => {
    const mapa = {};
    recurringExpenses
      .filter((e) => e.activo !== false && (!e.moneda || e.moneda.toUpperCase() === 'PEN'))
      .forEach((e) => {
        const key = normalizarCategoria(traducirCategoria(e.categoria || '', 'es'));
        if (!key) return;
        mapa[key] = (mapa[key] || 0) + (e.monto || 0);
      });
    return mapa;
  }, [recurringExpenses]);

  const filas = useMemo(() => {
    return budgets
      .map((b) => {
        const s = summary.find((x) => x.categoria === b.categoria);
        const gastado = s ? s.gastado : 0;
        const porcentaje = b.monto > 0 ? Math.min(100, Math.round((gastado / b.monto) * 100)) : 0;
        const ratio = b.monto > 0 ? gastado / b.monto : 0;
        const color = ratio >= 1 ? 'error' : ratio >= 0.8 ? 'warning' : 'success';
        const pisoRecurrentes = pisoRecurrentesPorCategoria[normalizarCategoria(traducirCategoria(b.categoria, 'es'))] || 0;
        return { ...b, gastado, porcentaje, color, pisoRecurrentes };
      })
      .sort((a, b) => b.porcentaje - a.porcentaje);
  }, [budgets, summary, pisoRecurrentesPorCategoria]);

  // 30 sep 2026 (Augusto: "en la zona de presupuesto, debería haber un
  // lugar que totalice lo que se viene gastando contra el total
  // presupuestado").
  //
  // La página mostraba cada categoría por separado y un pie con el gasto
  // total del mes. Faltaba lo que uno mira primero: ¿voy bien o mal en
  // conjunto? Con doce categorías, sumar de cabeza no es una opción.
  const totales = useMemo(() => {
    const presupuestado = filas.reduce((acc, f) => acc + (f.monto || 0), 0);
    const gastado = filas.reduce((acc, f) => acc + (f.gastado || 0), 0);
    const excedidas = filas.filter((f) => f.monto > 0 && f.gastado > f.monto);
    // Lo gastado en categorías SIN presupuesto. Va aparte y no se suma al
    // total presupuestado: meterlo adentro haría creer que está cubierto
    // por un límite que no existe.
    const fueraDePresupuesto = Math.max(0, gastoTotalMesSoles - gastado);
    return {
      presupuestado,
      gastado,
      restante: presupuestado - gastado,
      porcentaje: presupuestado > 0 ? Math.round((gastado / presupuestado) * 100) : 0,
      excedidas,
      fueraDePresupuesto,
    };
  }, [filas, gastoTotalMesSoles]);
  // Cuántos días quedan del mes. "Te quedan S/ 500" se lee muy distinto
  // con 3 días por delante que con 20.
  const diasRestantesMes = useMemo(() => {
    const hoy = new Date();
    const primeroSiguiente = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 1);
    return Math.ceil((primeroSiguiente - hoy) / 86400000);
  }, []);

  const [editandoId, setEditandoId] = useState(null);
  const [montoDraft, setMontoDraft] = useState('');
  const iniciarEdicion = (b) => {
    setEditandoId(b.id);
    setMontoDraft(String(b.monto));
  };
  const guardarEdicion = (id) => {
    const monto = parseFloat(montoDraft);
    if (monto > 0) dispatch(updateBudget({ id, data: { monto } }));
    setEditandoId(null);
  };

  const [nuevaCategoria, setNuevaCategoria] = useState(null);
  const [nuevoMonto, setNuevoMonto] = useState('');
  const [agregando, setAgregando] = useState(false);
  const handleAgregar = async () => {
    const categoria = (nuevaCategoria || '').trim();
    const monto = parseFloat(nuevoMonto);
    if (!categoria || !(monto > 0)) return;
    setAgregando(true);
    try {
      await dispatch(addBudget({ categoria, monto })).unwrap();
      setNuevaCategoria(null);
      setNuevoMonto('');
    } catch {
      // el error ya queda en state.budgets.error, se muestra abajo.
    } finally {
      setAgregando(false);
    }
  };

  const handleEliminar = (id) => {
    if (window.confirm(language === 'es' ? '¿Eliminar este presupuesto?' : 'Delete this budget?')) {
      dispatch(deleteBudget(id));
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        {language === 'es' ? 'Presupuesto' : 'Budget'}
      </Typography>
      {/* 29 ago 2026: el "por qué" del presupuesto, con las palabras del
          propio Augusto -- breve a propósito, esto no es un ensayo, es un
          recordatorio cada vez que se abre la página. */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 720 }}>
        {language === 'es'
          ? 'Ponle un tope mensual a cada categoría para no pasarte, y así llegar a tus metas de ahorro e inversión. Verlo cada mes es lo que hace más fácil cambiar el hábito.'
          : "Set a monthly cap per category so you don't overspend, and get closer to your savings and investment goals. Seeing it every month is what makes the habit easier to change."}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {typeof error === 'string' ? error : (error?.detail || (language === 'es' ? 'Ocurrió un error.' : 'Something went wrong.'))}
        </Alert>
      )}

      {/* 30 ago 2026 (pedido de Augusto: "lo que se necesita es la meta,
          que no está definida" -- ver savings_goal.py para el backend
          completo): meta de ahorro mensual + largo plazo, con su avance
          calculado por el servidor (ingresos reales menos gastos reales,
          nunca lo presupuestado -- decisión explícita de Augusto). */}
      <Paper sx={{ p: 2.5, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: goalEditing ? 2 : 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <SavingsIcon fontSize="small" color="primary" />
            {language === 'es' ? 'Meta de ahorro' : 'Savings goal'}
          </Typography>
          {!goalEditing && (
            <Button size="small" startIcon={<EditIcon fontSize="small" />} onClick={() => setGoalEditing(true)}>
              {goal && (goal.meta_mensual || goal.meta_largo_plazo_monto)
                ? (language === 'es' ? 'Editar meta' : 'Edit goal')
                : (language === 'es' ? 'Definir meta' : 'Set goal')}
            </Button>
          )}
        </Box>

        {goalEditing ? (
          <Box>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label={language === 'es' ? 'Meta de ahorro mensual (S/)' : 'Monthly savings goal (S/)'}
                  helperText={language === 'es' ? 'Cuánto quieres ahorrar cada mes.' : 'How much you want to save each month.'}
                  value={goalForm.meta_mensual}
                  onChange={(e) => setGoalForm((prev) => ({ ...prev, meta_mensual: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={goalForm.definirLargoPlazo}
                      onChange={(e) => setGoalForm((prev) => ({ ...prev, definirLargoPlazo: e.target.checked }))}
                    />
                  }
                  label={language === 'es' ? 'Definir meta a largo plazo (ahorro/inversión)' : 'Set a long-term goal (savings/investment)'}
                />
              </Grid>
              {goalForm.definirLargoPlazo && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label={language === 'es' ? 'Monto objetivo (S/)' : 'Target amount (S/)'}
                      value={goalForm.meta_largo_plazo_monto}
                      onChange={(e) => setGoalForm((prev) => ({ ...prev, meta_largo_plazo_monto: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="month"
                      label={language === 'es' ? 'Fecha objetivo' : 'Target date'}
                      InputLabelProps={{ shrink: true }}
                      value={goalForm.meta_largo_plazo_fecha ? goalForm.meta_largo_plazo_fecha.slice(0, 7) : ''}
                      onChange={(e) => setGoalForm((prev) => ({ ...prev, meta_largo_plazo_fecha: e.target.value ? `${e.target.value}-01` : '' }))}
                    />
                  </Grid>
                </>
              )}
            </Grid>
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button
                variant="contained"
                size="small"
                disabled={goalSaving}
                startIcon={goalSaving ? <CircularProgress size={14} color="inherit" /> : null}
                onClick={handleGuardarMeta}
              >
                {language === 'es' ? 'Guardar' : 'Save'}
              </Button>
              <Button
                size="small"
                disabled={goalSaving}
                onClick={() => {
                  setGoalEditing(false);
                  setGoalForm({
                    meta_mensual: goal?.meta_mensual ?? '',
                    definirLargoPlazo: !!goal?.meta_largo_plazo_monto,
                    meta_largo_plazo_monto: goal?.meta_largo_plazo_monto ?? '',
                    meta_largo_plazo_fecha: goal?.meta_largo_plazo_fecha ?? '',
                  });
                }}
              >
                {language === 'es' ? 'Cancelar' : 'Cancel'}
              </Button>
            </Box>
          </Box>
        ) : !goal || (!goal.meta_mensual && !goal.meta_largo_plazo_monto) ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {language === 'es'
              ? 'Todavía no definiste una meta de ahorro. Sin una meta, el presupuesto por categoría te ayuda a no pasarte, pero no te dice si eso alcanza para ahorrar lo que quieres.'
              : "You haven't set a savings goal yet. Without one, your category budgets help you avoid overspending, but they don't tell you if that's enough to save what you want."}
          </Typography>
        ) : (
          <Box sx={{ mt: 1.5 }}>
            {progress?.meta_mensual != null && (
              <Box sx={{ mb: goal.meta_largo_plazo_monto ? 2 : 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2">
                    {language === 'es' ? 'Este mes' : 'This month'} ({formatMesActual(language)}): S/{formatAmount(progress.ahorro_mes_actual)} {language === 'es' ? 'de' : 'of'} S/{formatAmount(progress.meta_mensual)}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {progress.porcentaje_mensual}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, progress.porcentaje_mensual)}
                  color={progress.ahorro_mes_actual < 0 ? 'error' : progress.porcentaje_mensual >= 100 ? 'success' : 'warning'}
                  sx={{ height: 8, borderRadius: 5 }}
                />
              </Box>
            )}
            {progress?.meta_largo_plazo_monto != null && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2">
                    {language === 'es' ? 'Acumulado' : 'Accumulated'}: S/{formatAmount(progress.ahorro_acumulado)} {language === 'es' ? 'de' : 'of'} S/{formatAmount(progress.meta_largo_plazo_monto)}
                    {progress.meses_restantes != null && (
                      <>
                        {' · '}
                        {progress.meses_restantes >= 0
                          ? (language === 'es' ? `faltan ${progress.meses_restantes} meses` : `${progress.meses_restantes} months left`)
                          : (language === 'es' ? 'fecha objetivo ya pasó' : 'target date already passed')}
                      </>
                    )}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {progress.porcentaje_largo_plazo}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, progress.porcentaje_largo_plazo)}
                  color={progress.porcentaje_largo_plazo >= 100 ? 'success' : 'primary'}
                  sx={{ height: 8, borderRadius: 5 }}
                />
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {/* 29 ago 2026 (pedido de Augusto: constructor de presupuesto con
          IA, exclusivo del plan Pro, con su propio costo en páginas):
          botón siempre visible -- si el usuario no tiene un plan con
          acceso, lleva a Suscripción en vez de llamar al backend. */}
      <Paper sx={{ p: 2.5, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {language === 'es' ? 'Constructor de presupuesto con IA' : 'AI budget builder'}
              {!tienePlanIA && (
                <Chip label={language === 'es' ? 'Plan Pro' : 'Pro plan'} size="small" color="primary" sx={{ ml: 1 }} />
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {language === 'es'
                ? 'La IA analiza tus últimos meses de gasto real por categoría y te sugiere un monto mensual, además de dónde tienes más margen para recortar.'
                : 'The AI looks at your last few months of real spending per category and suggests a monthly amount, plus where you have the most room to cut.'}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={aiLoading ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
            disabled={aiLoading}
            onClick={() => (tienePlanIA ? handleAiSuggest() : navigate('/subscription'))}
          >
            {tienePlanIA
              ? (aiLoading ? (language === 'es' ? 'Analizando...' : 'Analyzing...') : (language === 'es' ? 'Construir con IA' : 'Build with AI'))
              : (language === 'es' ? 'Disponible en Pro' : 'Available on Pro')}
          </Button>
        </Box>

        {aiError && (
          <Alert
            severity={aiPlanBlocked || aiQuotaExceeded ? 'info' : 'error'}
            sx={{ mt: 2 }}
            action={
              aiPlanBlocked ? (
                <Button color="inherit" size="small" onClick={() => navigate('/subscription')}>
                  {language === 'es' ? 'Ver planes' : 'See plans'}
                </Button>
              ) : aiQuotaExceeded ? (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button color="inherit" size="small" onClick={() => navigate('/subscription')}>
                    {language === 'es' ? 'Comprar páginas' : 'Buy pages'}
                  </Button>
                  {isAdminUser && (
                    <Button color="inherit" size="small" disabled={aiLoading} onClick={() => handleAiSuggest(true)}>
                      {language === 'es' ? 'Ignorar y continuar' : 'Ignore and continue'}
                    </Button>
                  )}
                </Box>
              ) : undefined
            }
          >
            {aiError}
          </Alert>
        )}

        {aiSugerencias.length > 0 && (
          <Box sx={{ mt: 2.5 }}>
            <Divider sx={{ mb: 2 }} />
            {aiResumen && (
              <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
                {aiResumen}
              </Typography>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
              <Button
                size="small"
                variant="outlined"
                disabled={aiAplicandoTodas}
                startIcon={aiAplicandoTodas ? <CircularProgress size={14} /> : null}
                onClick={aplicarTodasLasSugerencias}
              >
                {language === 'es' ? 'Usar todas las sugerencias' : 'Use all suggestions'}
              </Button>
            </Box>
            <Grid container spacing={2}>
              {aiSugerencias.map((sug) => (
                <Grid item xs={12} sm={6} key={sug.categoria}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {getCategoryEmoji(sug.categoria, emojiOverrides)} {traducirCategoria(sug.categoria, language)}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {sug.prioridad_de_recorte && (
                          <Chip
                            label={language === 'es' ? 'Más margen para recortar' : 'Most room to cut'}
                            size="small"
                            color="warning"
                          />
                        )}
                        {/* 30 ago 2026 (pedido de Augusto: "eliminar las
                            variables"): descarta esta tarjeta sin
                            aplicarla -- ver descartarSugerencia. */}
                        <Tooltip title={language === 'es' ? 'Descartar sugerencia' : 'Discard suggestion'}>
                          <IconButton
                            size="small"
                            disabled={aiAplicando === sug.categoria || aiAplicandoTodas}
                            onClick={() => descartarSugerencia(sug.categoria)}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {language === 'es' ? 'Promedio real' : 'Real average'}: S/{formatAmount(sug.promedio_mensual)}
                      {sug.presupuesto_actual != null && (
                        <> · {language === 'es' ? 'presupuesto actual' : 'current budget'}: S/{formatAmount(sug.presupuesto_actual)}</>
                      )}
                    </Typography>
                    <Typography variant="body2" sx={{ my: 1 }}>
                      {sug.razon}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                      {/* 30 ago 2026 (pedido de Augusto: "no permite
                          ajustar los montos... solo aprobar"): antes este
                          número era de solo lectura -- ahora es un campo
                          editable, precargado con el monto sugerido, que
                          el usuario puede corregir antes de aplicarlo (ver
                          montoParaSugerencia/montosEditados). */}
                      <TextField
                        size="small"
                        type="number"
                        label="S/"
                        value={montosEditados[sug.categoria] ?? sug.presupuesto_sugerido}
                        onChange={(e) =>
                          setMontosEditados((prev) => ({ ...prev, [sug.categoria]: e.target.value }))
                        }
                        disabled={aiAplicando === sug.categoria || aiAplicandoTodas}
                        inputProps={{ min: 0, step: 1 }}
                        sx={{ maxWidth: 130 }}
                      />
                      <Button
                        size="small"
                        variant="contained"
                        disabled={aiAplicando === sug.categoria || aiAplicandoTodas}
                        startIcon={aiAplicando === sug.categoria ? <CircularProgress size={14} color="inherit" /> : null}
                        onClick={() => aplicarSugerencia(sug)}
                      >
                        {language === 'es' ? 'Usar este monto' : 'Use this amount'}
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Agregar presupuesto nuevo */}
      <Paper sx={{ p: 2.5, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
          {language === 'es' ? 'Agregar presupuesto' : 'Add budget'}
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={categoriasDisponibles}
              getOptionLabel={(c) => `${getCategoryEmoji(c, emojiOverrides)} ${traducirCategoria(c, language)}`}
              value={nuevaCategoria}
              onChange={(e, value) => setNuevaCategoria(value)}
              renderInput={(params) => (
                <TextField {...params} label={language === 'es' ? 'Categoría' : 'Category'} size="small" />
              )}
              disabled={categoriasDisponibles.length === 0}
            />
          </Grid>
          <Grid item xs={8} sm={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label={language === 'es' ? 'Monto mensual (S/)' : 'Monthly amount (S/)'}
              value={nuevoMonto}
              onChange={(e) => setNuevoMonto(e.target.value)}
            />
          </Grid>
          <Grid item xs={4} sm={3}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleAgregar}
              disabled={agregando || !nuevaCategoria || !nuevoMonto}
              startIcon={agregando ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {language === 'es' ? 'Agregar' : 'Add'}
            </Button>
          </Grid>
        </Grid>
        {categoriasDisponibles.length === 0 && todasLasCategorias.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {language === 'es'
              ? 'Ya tienes un presupuesto para todas tus categorías.'
              : "You've already set a budget for all your categories."}
          </Typography>
        )}
      </Paper>

      {/* 30 ago 2026 (pedido de Augusto: "mostrar una tabla con todas las
          categorías y sus límites a ajustarse mensualmente" -- reemplaza
          las tarjetas de antes): una tabla es más compacta para ajustar
          varias categorías seguidas, y deja la barra de progreso como una
          columna más en vez de ser el elemento central de cada tarjeta.
          Mismo flujo de edición inline (editandoId/montoDraft) y mismo
          handleEliminar de siempre, solo cambia el layout. */}
      {!loading && filas.length > 0 && (
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          {language === 'es' ? 'Progreso de' : 'Progress for'} {formatMesActual(language)}
        </Typography>
      )}
      {loading ? (
        <Typography color="text.secondary">{language === 'es' ? 'Cargando...' : 'Loading...'}</Typography>
      ) : filas.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {language === 'es'
              ? 'Todavía no tienes ningún presupuesto. Agrega el primero arriba.'
              : "You don't have any budget yet. Add your first one above."}
          </Typography>
        </Paper>
      ) : (
        <>
        {/* 30 sep 2026: el resumen va ARRIBA de la tabla a propósito --
            es la respuesta a "¿cómo voy?", y esa pregunta se hace antes
            de mirar categoría por categoría. */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'flex-end', mb: 1.5 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                {language === 'es' ? 'Gastado este mes' : 'Spent this month'}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                S/{formatAmount(totales.gastado)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                {language === 'es' ? 'Presupuestado' : 'Budgeted'}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                S/{formatAmount(totales.presupuestado)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                {totales.restante >= 0
                  ? (language === 'es' ? 'Te queda' : 'Left')
                  : (language === 'es' ? 'Te pasaste por' : 'Over by')}
              </Typography>
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, lineHeight: 1.2 }}
                color={totales.restante >= 0 ? 'success.main' : 'error.main'}
              >
                S/{formatAmount(Math.abs(totales.restante))}
              </Typography>
            </Box>
            <Box sx={{ ml: 'auto', textAlign: 'right' }}>
              <Typography variant="caption" color="text.secondary" display="block">
                {language === 'es'
                  ? `${diasRestantesMes} día(s) de mes`
                  : `${diasRestantesMes} day(s) left`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {totales.porcentaje}% {language === 'es' ? 'del presupuesto' : 'of budget'}
              </Typography>
            </Box>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, totales.porcentaje)}
            color={totales.porcentaje >= 100 ? 'error' : totales.porcentaje >= 80 ? 'warning' : 'success'}
            sx={{ height: 10, borderRadius: 5 }}
          />
          {totales.excedidas.length > 0 && (
            <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 1.5 }}>
              {language === 'es'
                ? `Te pasaste en ${totales.excedidas.length} categoría(s): ${totales.excedidas.map((f) => f.categoria).join(', ')}.`
                : `Over budget in ${totales.excedidas.length} categor(ies): ${totales.excedidas.map((f) => f.categoria).join(', ')}.`}
            </Typography>
          )}
          {/* Un total que solo mira las categorías con presupuesto puede
              dar tranquilidad falsa: el gasto de verdad del mes incluye
              todo lo demás. Se dice, y se dice aparte. */}
          {totales.fueraDePresupuesto > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {language === 'es'
                ? `Además gastaste S/${formatAmount(totales.fueraDePresupuesto)} en categorías sin presupuesto. Gasto total del mes: S/${formatAmount(gastoTotalMesSoles)}.`
                : `You also spent S/${formatAmount(totales.fueraDePresupuesto)} in categories with no budget. Total spent this month: S/${formatAmount(gastoTotalMesSoles)}.`}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {language === 'es'
              ? 'Solo se cuentan gastos en soles: un presupuesto en soles no se consume con una compra en dólares.'
              : 'Only expenses in soles count: a budget in soles is not consumed by a purchase in dollars.'}
          </Typography>
        </Paper>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{language === 'es' ? 'Categoría' : 'Category'}</TableCell>
                <TableCell align="right">{language === 'es' ? 'Presupuesto mensual' : 'Monthly budget'}</TableCell>
                <TableCell align="right">{language === 'es' ? 'Gastado este mes' : 'Spent this month'}</TableCell>
                <TableCell align="right">{language === 'es' ? 'Restante / excedido' : 'Left / over'}</TableCell>
                <TableCell sx={{ minWidth: 120 }}>{language === 'es' ? 'Progreso' : 'Progress'}</TableCell>
                <TableCell align="right">{language === 'es' ? 'Acciones' : 'Actions'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filas.map((b) => (
                <TableRow key={b.id} hover>
                  <TableCell>
                    {getCategoryEmoji(b.categoria, emojiOverrides)} {traducirCategoria(b.categoria, language)}
                  </TableCell>
                  <TableCell align="right">
                    {editandoId === b.id ? (
                      <TextField
                        size="small"
                        type="number"
                        autoFocus
                        value={montoDraft}
                        onChange={(e) => setMontoDraft(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && guardarEdicion(b.id)}
                        sx={{ maxWidth: 110 }}
                      />
                    ) : (
                      `S/${formatAmount(b.monto)}`
                    )}
                    {/* 30 ago 2026 (pregunta de Augusto sobre recurrentes
                        fijos y presupuesto): referencia informativa de
                        cuánto de este presupuesto ya está comprometido por
                        pagos fijos conocidos (alquiler, suscripciones...).
                        A propósito SIN color de alerta -- Augusto: "no es
                        necesario que se coloque la alerta de color en esas
                        dado que esas nunca se van a pasar". */}
                    {b.pisoRecurrentes > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {language === 'es' ? 'Piso fijo' : 'Fixed floor'}: S/{formatAmount(b.pisoRecurrentes)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">S/{formatAmount(b.gastado)}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color={b.color === 'error' ? 'error' : 'text.secondary'} component="span">
                      {b.gastado > b.monto
                        ? `-S/${formatAmount(b.gastado - b.monto)}`
                        : `S/${formatAmount(b.monto - b.gastado)}`}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <LinearProgress
                      variant="determinate"
                      value={b.porcentaje}
                      color={b.color}
                      sx={{ height: 8, borderRadius: 5 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {editandoId === b.id ? (
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Button size="small" variant="contained" onClick={() => guardarEdicion(b.id)}>
                          {language === 'es' ? 'Guardar' : 'Save'}
                        </Button>
                        <Button size="small" onClick={() => setEditandoId(null)}>
                          {language === 'es' ? 'Cancelar' : 'Cancel'}
                        </Button>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Tooltip title={language === 'es' ? 'Editar monto' : 'Edit amount'}>
                          <IconButton size="small" onClick={() => iniciarEdicion(b)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={language === 'es' ? 'Eliminar' : 'Delete'}>
                          <IconButton size="small" color="error" onClick={() => handleEliminar(b.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        </>
      )}
    </Box>
  );
};

export default Budgets;
